import { useContext, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  IPersonTypeFilterSetting,
  QueryFilter,
  PersonFilterKeys,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { KodeSelect } from "../../../formcontrols/KodeSelect";
import {
  PersonDtoFilterInput,
  PersonPersontypeFilterInput,
} from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import DateFilter from "../../../filter/date/DateFilter";
import { DateFilterEvent } from "../../../filter/date/types";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "personType";

export default function PersonTypeFilters() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: IPersonTypeFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  // stuff
  const isDateDisabled = Boolean(!settings?.activeFilters?.length);

  // handlers
  function updateFilterHandler(
    selectedItem: KodeListe | null,
    dateFilterEvent?: DateFilterEvent,
  ) {
    // get active list if any otherwise initialise an empty array
    const activeFilters = settings?.activeFilters || [];

    // add selected item if it does not exist in active list
    if (
      selectedItem &&
      !activeFilters.some((s) => s.kode === selectedItem.nr)
    ) {
      activeFilters.push({
        kode: selectedItem.nr,
        type: selectedItem.navn,
      });
    }

    // check for valid date event otherwise assign latest date event
    if (!dateFilterEvent) {
      dateFilterEvent = latestDateEvent;
    } else {
      setLatestDateEvent(dateFilterEvent);
    }

    const filters: PersonPersontypeFilterInput[] = activeFilters.map((set) => {
      return {
        and: [
          {
            persontypeNr: {
              eq: set.kode,
            },
          },
          {
            periode: dateFilterEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personPersontype", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonTypeFilterSetting = {
      activeFilters: activeFilters,
      dateFilterSettings: dateFilterEvent.settings,
    };

    const queryFilter: QueryFilter<PersonFilterKeys, IPersonTypeFilterSetting> =
      {
        filter: filterString,
        filterKey: filterKey,
        settings: updatedSettings,
        parentFilter: "and",
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(
    kode: IPersonTypeFilterSetting["activeFilters"][number]["kode"],
  ) {
    const updatedFilters =
      settings?.activeFilters?.filter((set) => {
        return kode !== set.kode;
      }) || [];

    const filters: PersonPersontypeFilterInput[] = updatedFilters.map((set) => {
      return {
        and: [
          {
            persontypeNr: {
              eq: set.kode,
            },
          },
          {
            periode: latestDateEvent?.filter,
          },
        ],
      };
    });

    // build the filter string
    const filterString = f<PersonDtoFilterInput>()
      .addFilter("personPersontype", {
        some: {
          or: filters,
        },
      })
      .build();

    const updatedSettings: IPersonTypeFilterSetting = {
      activeFilters: updatedFilters,
      dateFilterSettings: latestDateEvent.settings,
    };

    const update = updatedSettings?.activeFilters?.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Persontype"
      value="persontype"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings.activeFilters.map((filter) => (
            <AppliedFilterItem
              key={filter.kode}
              label={filter.type}
              removeFilterHandler={() => removeFilterHandler(filter.kode)}
            />
          ))}
        </div>
      )}

      <KodeSelect
        kodeListeType="Persontype"
        placeholder="Vælg persontype"
        clearAfterSelection
        onChange={(item) => {
          updateFilterHandler(item);
        }}
      />
      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) => {
            updateFilterHandler(null, dateFilterEvent);
          }}
          settings={settings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(settings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
